import React from 'react';
import { AddressManagerScreen } from 'src/components/AddressManager';
import { ChatComponent } from 'src/components/Chat';
import { NotificationComponentScreen } from 'src/components/NotificationComponent';
import OrderPreparation from 'src/components/OrderPreparation';
import { OrderTracking } from 'src/components/OrderTracking';
import { PrivacySettings } from 'src/components/PrivacySettings';
import { ProfileSideMenu } from 'src/components/ProfileSideMenu';
import { StoreProfileManager } from 'src/components/StoreProfileManager';
import { UserProfileManagerScreen } from 'src/components/UserProfileManager';
import { AuthWrapper } from 'src/features/core/auth/screen/AuthWrapper';
import { CreateProductScreen } from 'src/features/createProduct/screens/CreateProduct';
import { SellerBalanceSummary } from 'src/features/sell/screens/SellerBalanceSummary';
import { SellFormScreen } from 'src/features/sell/screens/SellFormScreen';
import { CreditCardPaymentAddScreen } from 'src/features/userProfile/components/CreditCards';
import { ProductManagerScreen } from 'src/features/userProfile/components/ProductManager';
import { AlertMessage } from 'src/model/AlertMessage';
import Client from 'src/model/User';
import { UserType } from 'src/model/UserType';

interface UserProfileUIProps {
  user: Client;
  notifications: AlertMessage[];
}

export const UserProfileUI: React.FC<UserProfileUIProps> = ({
  user,
  notifications,
}) => {
  const isClient = user.type === UserType.CLIENT;

  const allMenuItems = [
    user.sellerId && {
      title: 'Resumo da Conta',
      key: 'resumo-conta',
      content: <SellerBalanceSummary sellerId={user.sellerId} />,
    },
    isClient && {
      title: 'Vender',
      key: 'form-vender',
      content: <SellFormScreen />,
    },
    {
      title: 'Chat',
      key: 'chat',
      content: <ChatComponent user={user} />,
    },
    {
      title: 'Alterar Dados Pessoais',
      key: 'dados-pessoais',
      content: <UserProfileManagerScreen userData={user} />,
    },
    {
      title: 'Dados Loja',
      key: 'dados-loja',
      content: <StoreProfileManager />,
    },
    {
      title: 'Criar Produto',
      key: 'criar-produto',
      content: <CreateProductScreen />,
    },
    {
      title: 'Gerenciar Produtos',
      key: 'gerenciar-produtos',
      content: <ProductManagerScreen />,
      desktopOnly: true,
    },
    {
      title: 'Acompanhar Pedidos',
      key: 'acompanhar-pedidos',
      content: <OrderTracking />,
    },
    {
      title: 'Cartões',
      key: 'cartoes',
      content: <CreditCardPaymentAddScreen />,
    },
    {
      title: 'Endereços',
      key: 'enderecos',
      content: <AddressManagerScreen address={user.address || []} />,
    },
    {
      title: 'Notificações',
      key: 'notificacoes',
      content: (
        <NotificationComponentScreen
          alertMessage={notifications}
          variant="page"
        />
      ),
    },
    {
      title: 'Vendas',
      key: 'vendas',
      content: <OrderPreparation />,
    },
    {
      title: 'Config de Privacidade',
      key: 'config-privacidade',
      content: <PrivacySettings />,
    },
  ].filter(Boolean) as any[];

  const clientVisibleKeys = [
    'form-vender',
    'chat',
    'dados-pessoais',
    'acompanhar-pedidos',
    'cartoes',
    'enderecos',
    'notificacoes',
    'config-privacidade',
  ];

  const menuItems = isClient
    ? allMenuItems.filter((item) => clientVisibleKeys.includes(item.key))
    : allMenuItems;

  return (
    <AuthWrapper>
      <div className="w-screen bg-white min-h-screen overflow-hidden">
        <div className="relative bg-white h-36 sm:h-64 flex items-center justify-center border-b border-gray-200">
          <img
            src={
              user.image
                ? `${process.env.REACT_APP_BASE_URL}/uploads/${user.image}`
                : 'https://via.placeholder.com/100'
            }
            alt={`${user.name} Logo`}
            className="w-32 h-32 rounded border-4 border-white shadow-lg absolute top-[45%] sm:top-[65%]"
          />
        </div>

        <div className="flex flex-col items-center justify-center mt-20 text-center space-y-6">
          <div className="w-[90%] sm:w-[60%] flex justify-center items-center rounded bg-[#eeeeee86] px-4 py-6">
            <div className="text-center font-bold text-lg">{user.name}</div>
          </div>
        </div>

        <section className="flex-1 min-h-0 mt-10 mb-8 md:px-8 overflow-y-auto">
          <ProfileSideMenu user={user} menuItems={menuItems} />
        </section>
      </div>
    </AuthWrapper>
  );
};
